<template>
  <div class="space-y-4">
    <!-- Freshness cards — each an independent widget that fires its OWN query.
         A slow node (e.g. ADT) only spins its own card; the others render as
         soon as their answer returns. No shared loading/error gate. -->
    <div class="grid gap-3" :style="{ gridTemplateColumns: `repeat(${nodes.length || 1}, 1fr)` }">
      <FreshnessCard
        v-for="node in nodes"
        :key="node.key"
        :node-key="node.key"
        :label="node.label"
        :description="node.description"
        :entity-id="entityId"
      />
    </div>

    <!-- Data Feed Registry — independent tile, own request -->
    <FeedRegistryPanel />

    <!-- Claims pipeline chart — independent tile, own request -->
    <ClaimsPipelineChart :entity-id="entityId" />
  </div>
</template>

<script>
import axios from 'axios';
import FreshnessCard from '@/components/InsightsNew/FreshnessCard.vue';
import FeedRegistryPanel from '@/components/InsightsNew/FeedRegistryPanel.vue';
import ClaimsPipelineChart from '@/components/InsightsNew/ClaimsPipelineChart.vue';

/**
 * Data Completeness dashboard container.
 *
 * A thin shell only. Each data-completeness question is answered by its OWN
 * independent widget (FreshnessCard × N, FeedRegistryPanel, ClaimsPipelineChart),
 * each of which mounts, fires its own query, and owns its own loading/error/retry.
 * The container itself only fetches the (cheap, no-Snowflake) node list so it can
 * lay out the cards immediately.
 */
export default {
  name: 'DataFreshnessDashboard',
  components: { FreshnessCard, FeedRegistryPanel, ClaimsPipelineChart },
  props: {
    entityId: { type: Number, default: null },
    year: { type: Number, default: null },
  },
  data() {
    return {
      nodes: [],
    };
  },
  mounted() {
    this.fetchNodes();
  },
  methods: {
    async fetchNodes() {
      try {
        const resp = await axios.get('api/data-freshness/nodes/');
        this.nodes = resp.data || [];
      } catch (e) {
        // Fallback to the known node set so cards still render (and fetch their
        // own data) even if the metadata endpoint hiccups.
        this.nodes = [
          { key: 'eob', label: 'Claims', description: 'Most recent claim date of service received from CMS' },
          { key: 'labs', label: 'Labs', description: 'Most recent lab result service date from EHR' },
          { key: 'medication', label: 'Medications', description: 'Most recent medication start date from EHR' },
          { key: 'appointment', label: 'Appointments', description: 'Most recent scheduled appointment date from EHR' },
          { key: 'encounter', label: 'Encounters', description: 'Most recent encounter date from EHR' },
          { key: 'adt', label: 'ADT', description: 'Most recent hospital admit/discharge/transfer event received' },
        ];
      }
    },
  },
};
</script>
